import { useState } from "react";
import { CheckCircle2, Upload, X } from "lucide-react";
import { useLang } from "@/i18n";
import { toast } from "sonner";

export const todayISO = () => new Date().toISOString().slice(0, 10);
const ALLOWED_FILE = /\.(jpe?g|png|webp|heic|heif|mp4|mov)$/i;

export const Field = ({ label, children, required }) => (
  <div>
    <label className="label-dark">{label}{required && <span className="text-[#E11D48]"> *</span>}</label>
    {children}
  </div>
);

export const Input = (props) => <input {...props} className="input-dark" />;

export const Textarea = (props) => <textarea rows={3} {...props} className="input-dark resize-none" />;

export const Select = ({ options = [], ...props }) => (
  <select {...props} className="input-dark">
    {options.map((o) => <option key={o} value={o} className="bg-[#121215]">{o}</option>)}
  </select>
);

export const Honeypot = ({ value, onChange }) => (
  <input
    type="text" name="website" value={value} onChange={onChange}
    tabIndex={-1} autoComplete="off" aria-hidden="true"
    style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }}
  />
);

export const Gdpr = ({ checked, onChange }) => {
  const { t } = useLang();
  return (
    <label className="flex items-start gap-2.5 cursor-pointer" data-testid="gdpr-checkbox">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded accent-[#E11D48]" required />
      <span className="text-xs text-zinc-400 leading-relaxed">{t("form.gdpr")}</span>
    </label>
  );
};

export const PhotoUpload = ({ files, setFiles, testid = "photo-upload" }) => {
  const add = (e) => {
    const chosen = Array.from(e.target.files || []);
    const valid = [];
    chosen.forEach((f) => {
      if (!ALLOWED_FILE.test(f.name)) toast.warning(`${f.name}: formaat niet ondersteund`);
      else if (f.size > 8 * 1024 * 1024) toast.warning(`${f.name}: te groot (max 8MB)`);
      else valid.push(f);
    });
    setFiles((prev) => [...prev, ...valid].slice(0, 5));
    e.target.value = "";
  };
  return (
    <div data-testid={testid}>
      <label className="flex items-center justify-center gap-2 border border-dashed border-zinc-600 hover:border-[#E11D48] rounded-xl py-5 cursor-pointer text-sm text-zinc-400 hover:text-zinc-200 transition-colors">
        <Upload size={16} />
        <span>{files.length}/5</span>
        <input type="file" accept="image/*,video/mp4,video/quicktime" multiple className="hidden" onChange={add} data-testid={`${testid}-input`} />
      </label>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {files.map((f, i) => (
            <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-zinc-700">
              {f.type.startsWith("image/") ? (
                <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[9px] text-zinc-400 bg-zinc-800 p-1 text-center">{f.name}</div>
              )}
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}
                className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5" data-testid={`${testid}-remove-${i}`}>
                <X size={12} className="text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const SuccessBox = ({ message, testid = "form-success" }) => (
  <div data-testid={testid} className="card-dark border-green-700/50 p-6 text-center">
    <CheckCircle2 size={40} className="mx-auto text-[#22C55E] mb-3" />
    <p className="text-zinc-200 font-medium leading-relaxed">{message}</p>
  </div>
);

export const SubmitBtn = ({ loading, label, testid = "form-submit-btn" }) => (
  <button type="submit" disabled={loading} data-testid={testid}
    className="btn-red w-full disabled:opacity-50 disabled:cursor-not-allowed">
    {loading ? "..." : label}
  </button>
);

export function useFormState(initial) {
  const [data, setData] = useState(initial);
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target ? e.target.value : e }));
  return [data, set, setData];
}
